import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ReactComponent as SearchIcon } from "../../../Assets/iconoir_search.svg";
import useSearchProducts from "../Merchant/lib/fetchSearchProduct";
import { getProductUrl } from "../libs/getProductUrl";
import ProductPriceDisplay from "../components/ProductPriceDisplay";
import "../components/ProductCard.css";
import "./MarketSearch.css";

const SKELETON_ROWS = 4;

const SearchSkeleton = () => (
  <ul className="market-search__results market-search__results--skeleton" aria-hidden="true">
    {Array.from({ length: SKELETON_ROWS }).map((_, index) => (
      <li key={index} className="market-search__skeleton-row">
        <span className="market-search__skeleton-img" />
        <span className="market-search__skeleton-lines">
          <span className="market-search__skeleton-line market-search__skeleton-line--title" />
          <span className="market-search__skeleton-line market-search__skeleton-line--meta" />
          <span className="market-search__skeleton-line market-search__skeleton-line--price" />
        </span>
      </li>
    ))}
  </ul>
);

const MarketSearch = ({ className = "" }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();
  const trimmed = searchQuery.trim();
  const { isLoading, error, searchResults } = useSearchProducts(searchQuery, {
    limit: 8,
    minLength: 2,
    enabled: trimmed.length >= 2,
    retryKey,
  });

  const products = searchResults?.products || [];
  const totalCount = searchResults?.count ?? products.length;
  const showDropdown = dropdownOpen && trimmed.length >= 2;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (e) => {
    setSearchQuery(e.target.value);
    setDropdownOpen(true);
  };

  const handleFocus = () => {
    if (trimmed.length >= 2) {
      setDropdownOpen(true);
    }
  };

  const handleClear = () => {
    setSearchQuery("");
    setDropdownOpen(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const q = trimmed;
    if (!q) return;
    setDropdownOpen(false);
    navigate(`/market/search?q=${encodeURIComponent(q)}`);
  };

  const handleResultClick = () => {
    setDropdownOpen(false);
  };

  const handleViewAll = () => {
    if (!trimmed) return;
    setDropdownOpen(false);
    navigate(`/market/search?q=${encodeURIComponent(trimmed)}`);
  };

  const handleRetry = () => {
    setRetryKey((key) => key + 1);
  };

  return (
    <div className={`market-search ${className}`.trim()} ref={wrapperRef}>
      <form className="market-search__form" onSubmit={handleSubmit} role="search">
        <span className="market-search__icon" aria-hidden="true">
          <SearchIcon />
        </span>
        <input
          type="search"
          className="market-search__input"
          role="combobox"
          placeholder="Search products, brands, categories…"
          value={searchQuery}
          onChange={handleChange}
          onFocus={handleFocus}
          aria-label="Search products"
          aria-autocomplete="list"
          aria-expanded={showDropdown}
          aria-controls="market-search-dropdown"
          autoComplete="off"
        />
        {trimmed.length > 0 && (
          <button
            type="button"
            className="market-search__clear"
            onClick={handleClear}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
        <button type="submit" className="market-search__submit" aria-label="Search">
          Search
        </button>
      </form>

      {showDropdown && (
        <div
          id="market-search-dropdown"
          className="market-search__dropdown"
          role="listbox"
          aria-label="Search suggestions"
        >
          <div className="market-search__dropdown-head">
            <span className="market-search__dropdown-label">
              {isLoading ? "Searching…" : `Results for “${trimmed}”`}
            </span>
            {!isLoading && !error && totalCount > 0 && (
              <span className="market-search__dropdown-count">
                {totalCount} found
              </span>
            )}
          </div>

          {isLoading && <SearchSkeleton />}

          {!isLoading && error && (
            <div className="market-search__panel market-search__panel--error">
              <p className="market-search__panel-title">Search unavailable</p>
              <p className="market-search__panel-text">{error}</p>
              <button type="button" className="market-search__retry" onClick={handleRetry}>
                Try again
              </button>
            </div>
          )}

          {!isLoading && !error && products.length === 0 && (
            <div className="market-search__panel market-search__panel--empty">
              <p className="market-search__panel-title">No matches</p>
              <p className="market-search__panel-text">
                We couldn&apos;t find products for “{trimmed}”. Try another keyword or browse categories.
              </p>
            </div>
          )}

          {!isLoading && !error && products.length > 0 && (
            <>
              <ul className="market-search__results">
                {products.map((product) => (
                  <li key={product._id}>
                    <Link
                      to={getProductUrl(product)}
                      className="market-search__result"
                      onClick={handleResultClick}
                    >
                      <img
                        src={product.images?.[0] || "/placeholder-product.png"}
                        alt=""
                        className="market-search__result-img"
                      />
                      <span className="market-search__result-body">
                        <span className="market-search__result-name">{product.name}</span>
                        {product.category && (
                          <span className="market-search__result-meta">{product.category}</span>
                        )}
                        <ProductPriceDisplay
                          product={product}
                          size="inline"
                          className="market-search__result-price"
                        />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
              <button type="button" className="market-search__view-all" onClick={handleViewAll}>
                View all {totalCount > products.length ? `${totalCount} ` : ""}results
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MarketSearch;
